import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import moment from "moment-timezone";
import { ALERT, USER } from "@constants";
import { updateUser } from "@actions/user";
import {
  getCanonicalTimezone,
  getTradingViewTimezone,
  getUserTimezone,
  upsertTradingViewTimezone,
} from "@helpers/timezone";

/**
 * Time zone of the profile (E24): the chart has no zone of its own. Saving
 * goes the way of the profile page: POST /users/update through updateUser,
 * the user in Redux and the zone the profile page reads first
 * (upsertTradingViewTimezone). The zone is read in the profile page's order
 * (useProfileUserData): the one saved in this browser first; a zone saved on
 * the server is copied there when the user loads (syncSavedTimezone). Saving updates the Redux user, so the chart
 * re-renders with the new zone.
 *
 * @returns {{
 *   timezone: string,
 *   saveTimezone: (timezone: string) => Promise<void>,
 *   isSaving: boolean,
 * }} timezone: IANA zone of the profile, the browser's one until the profile has it
 */
const useProfileTimezone = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const profileTimezone = useSelector((state) => state.user.user?.timezone || null);
  const [isSaving, setIsSaving] = useState(false);

  // The zone of the profile also rules the dates outside the chart: History and
  // wallet tables read "userTimezone", the rest of the app uses the moment default
  useEffect(() => {
    if (!profileTimezone) return;

    try {
      moment.tz.setDefault(profileTimezone);
      localStorage.setItem("userTimezone", profileTimezone);
    } catch (error) {
      // The storage may be unavailable, the chart keeps working without it
    }
  }, [profileTimezone]);

  // profileTimezone is a dependency so the zone is read again once it is saved
  const timezone = useMemo(
    () => getCanonicalTimezone(getTradingViewTimezone() || profileTimezone || getUserTimezone()),
    [profileTimezone],
  );

  const saveTimezone = useCallback(
    async (nextTimezone) => {
      if (!nextTimezone || nextTimezone === timezone) return;

      setIsSaving(true);

      try {
        const response = await dispatch(updateUser({ data: { timezone: nextTimezone } }));
        const savedTimezone = response?.data?.user?.timezone ?? nextTimezone;

        // As the profile page does: the answer may come without the user
        dispatch({ type: USER.USER_FIELD, payload: { timezone: savedTimezone } });
        upsertTradingViewTimezone(savedTimezone);
      } catch (error) {
        // updateUser shows the message of the server when there is one
        if (!error.response?.data?.message) {
          dispatch({
            type: ALERT.ALERT_ERROR,
            payload: intl.formatMessage({ id: "common.failed" }),
          });
        }
      } finally {
        setIsSaving(false);
      }
    },
    [dispatch, intl, timezone],
  );

  return { timezone, saveTimezone, isSaving };
};

export default useProfileTimezone;
